import { createContext, useContext, useEffect } from 'react';

import type { PaletteAction } from './CommandPalette';

/**
 * The seam that lets the CURRENT PAGE contribute commands to the app's one ⌘K
 * palette. Context + hooks live here (no component), so the provider can stay a
 * component file — the same split `useCommandActions` already uses, and what
 * keeps react-refresh happy.
 *
 * ## Why this exists
 *
 * The palette's global actions self-source their deps (`useCommandActions`) and
 * its destinations come from the sidebar — both app-wide, both known to the
 * shell. Neither can express a command that only makes sense on the page you are
 * looking at ("focus this endpoint", "widen by one hop"), because the shell has
 * no idea what is on screen.
 *
 * Without this seam the tempting move is a SECOND palette owned by the page —
 * which `/map` briefly grew, and it was wrong twice over: it duplicated a
 * component that already works, and both palettes bound ⌘K, so one keystroke
 * opened two dialogs stacked on each other. One palette, fed from more than one
 * place.
 *
 * ## The contract
 *
 * A page calls {@link useRegisterPaletteActions} with its commands; they appear
 * while that page is mounted and vanish with it. Registration is keyed by a
 * caller-chosen `sourceId`, so a page can re-publish freely as its state changes
 * without ever leaving a stale copy behind.
 */

export interface PaletteRegistry {
  readonly actions: readonly PaletteAction[];
  readonly publish: (sourceId: string, actions: readonly PaletteAction[]) => void;
  readonly retract: (sourceId: string) => void;
}

export const PaletteRegistryContext = createContext<PaletteRegistry | null>(null);

/**
 * Publish `actions` to the ⌘K palette for as long as the caller is mounted.
 *
 * **`actions` MUST be memoized by the caller.** They are rebuilt from live state
 * on every render, so an unmemoized array would re-publish in a loop. That is the
 * price of letting a page publish live commands, and it is paid once, at the call
 * site.
 *
 * Outside a provider this is a no-op rather than a throw: a page rendered in a
 * test harness (or any future shell without the provider) should render, not
 * crash, over a palette it does not have.
 */
export function useRegisterPaletteActions(
  sourceId: string,
  actions: readonly PaletteAction[],
): void {
  const registry = useContext(PaletteRegistryContext);
  const publish = registry?.publish;
  const retract = registry?.retract;

  useEffect(() => {
    if (publish === undefined || retract === undefined) return undefined;
    publish(sourceId, actions);
    return () => {
      retract(sourceId);
    };
  }, [sourceId, actions, publish, retract]);
}

/** Every action the mounted pages have published. Read by the shell's palette. */
export function useRegisteredPaletteActions(): readonly PaletteAction[] {
  return useContext(PaletteRegistryContext)?.actions ?? [];
}
